import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronRight from '@mui/icons-material/ChevronRight';
import classNames from 'classnames';
import { ReactElement } from 'react';

import { useStoreActions } from '~/store/hooks';
import { IYearMonth, toYearMonth } from '~/utils/date/date';
import { useApiRequest } from '~/hooks/api';
import { ICeClientRequest } from '~/services/api/communityEngagement/types';

import { describeCeChip, formatCeChip } from './formatCeChip';

import styles from './CeChip.module.scss';

interface ICeChipProps {
  client: ICeClientRequest;
  month?: IYearMonth;
  onOpen?: () => void;
}

const CeChip = ({ client, month, onOpen }: ICeChipProps): ReactElement => {
  const { year, month: monthNumber } = month ?? toYearMonth(new Date());

  const onGetMonthlyTotals = useStoreActions(
    actions => actions.communityEngagement.onGetMonthlyTotals,
  );

  const { isLoading, data: totals } = useApiRequest(
    ({ signal }) => onGetMonthlyTotals({ ...client, year, month: monthNumber, signal }),
    { initialLoading: true, dependencies: [client.clinicId, client.clientId, year, monthNumber] },
  );

  if (isLoading) return <CircularProgress size={16} aria-label="Loading community engagement" />;
  if (!totals) return <span className={styles.unavailable}>Unavailable</span>;

  if (!onOpen) {
    return (
      <Chip
        label={formatCeChip(totals)}
        role="img"
        aria-label={describeCeChip(totals)}
        variant="outlined"
        className={classNames(styles.chip, styles.static)}
      />
    );
  }

  return (
    <Chip
      label={
        <>
          {formatCeChip(totals)}
          <ChevronRight className={styles.chevron} />
        </>
      }
      aria-label={`${describeCeChip(totals)} — open record`}
      variant="outlined"
      onClick={onOpen}
      className={styles.chip}
    />
  );
};

export default CeChip;
